/**
 * Service for generating and managing unique user IDs
 * Uses FingerprintJS for industry-standard browser fingerprinting
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs';

class UserService {
  private static readonly STORAGE_KEY = 'rateMyPlate_userId';
  private static userId: string | null = null;
  private static fpPromise: Promise<any> | null = null;

  /**
   * Gets or creates a unique user ID for this device/browser
   * The ID persists across sessions and is unique per device/browser combination
   */
  static async getUserId(): Promise<string> {
    if (this.userId) {
      return this.userId;
    }

    // Try to get existing ID from localStorage
    const storedId = localStorage.getItem(this.STORAGE_KEY);
    if (storedId) {
      this.userId = storedId;
      return this.userId;
    }

    // Generate new ID using FingerprintJS
    const newId = await this.generateFingerprintId();
    
    // Store in localStorage for persistence
    localStorage.setItem(this.STORAGE_KEY, newId);
    this.userId = newId;
    
    return this.userId;
  }

  /**
   * Generates a unique ID using FingerprintJS
   * This creates a consistent ID for the same device/browser combination
   */
  private static async generateFingerprintId(): Promise<string> {
    try {
      // Initialize FingerprintJS if not already done
      if (!this.fpPromise) {
        this.fpPromise = FingerprintJS.load();
      }

      const fp = await this.fpPromise;
      const result = await fp.get();
      
      // Use the visitor ID from FingerprintJS
      return `user_${result.visitorId}`;
    } catch (error) {
      console.warn('FingerprintJS failed, falling back to simple ID:', error);
      // Fallback to a simple random ID if FingerprintJS fails
      return `user_fallback_${Math.random().toString(36).substr(2, 9)}_${Date.now().toString(36)}`;
    }
  }

  /**
   * Synchronous version that returns cached ID or generates a temporary one
   * Use this when you need an immediate ID but can't wait for async generation
   */
  static getUserIdSync(): string {
    if (this.userId) {
      return this.userId;
    }

    // Try to get existing ID from localStorage
    const storedId = localStorage.getItem(this.STORAGE_KEY);
    if (storedId) {
      this.userId = storedId;
      return this.userId;
    }

    // Return a temporary ID that will be replaced when async generation completes
    const tempId = `user_temp_${Math.random().toString(36).substr(2, 9)}`;
    return tempId;
  }

  /**
   * Resets the user ID (useful for testing or if user wants to reset)
   */
  static resetUserId(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.userId = null;
    this.fpPromise = null;
  }

  /**
   * Gets the current user ID without generating a new one
   */
  static getCurrentUserId(): string | null {
    return this.userId || localStorage.getItem(this.STORAGE_KEY);
  }
}

export default UserService;
